import React, { useEffect, useMemo, useState } from "react";
import MaterialTable from "material-table";
import {
    Chip,
    makeStyles,
    CircularProgress,
    Card,
    CardContent,
    Typography,
    Grid,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SyncIcon from "@material-ui/icons/Sync";
import ScheduleIcon from "@material-ui/icons/Schedule";
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import SendIcon from "@material-ui/icons/Send";
import { confirmAlert } from "react-confirm-alert";
import { FlexibleWidthXYPlot, VerticalGridLines, HorizontalGridLines, XAxis, YAxis, HorizontalBarSeries, Hint } from "react-vis";


import { medicalApi } from "../../api/medical";
import incident_manager from '../../api/incident'

const useStyles = makeStyles(() => ({
    responded: {
        backgroundColor: "#e8f5e9",
        color: "#2e7d32",
    },
    sent: {
        backgroundColor: "#e3f2fd",
        color: "#1565c0",
    },
    reminded: {
        backgroundColor: "#fff8e1",
        color: "#ef6c00",
    },
    cardTitle: {
        fontWeight: "bold",
        marginBottom: 8,
    },
    cardMetric: {
        marginBottom: 4,
    },
}));

export default function SurveyStatusOverviewTable() {
    const classes = useStyles();

    const [surveyData, setSurveyData] = useState([]);
    const [medicalStats, setMedicalStats] = useState(null);
    const [season, setSeason] = useState("2026");
    const [loadingSync, setLoadingSync] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const data = await medicalApi.getSurveyStatusOverview(season);
            if (data) {
                setSurveyData(data);
            }
        };

        const fetchStats = async () => {
            const stats = await medicalApi.getSurveyInjuryStats(season);
            if (stats) {
                setMedicalStats(stats);
            }
        };

        fetchData();
        fetchStats();
    }, [season]);

    const tableData = useMemo(() => {
        if (!medicalStats) return surveyData;

        return surveyData.map((item) => ({
            ...item,
            fullName: `${item.firstName?.trim()} ${item.lastName}`,
            status: item.responseDate
                ? "Responded"
                : item.reminderDate
                    ? "Reminder Sent"
                    : "Sent",
            ciss: medicalStats.incidents.find(i => i.incident_id === item.incidentId)?.CISS || "N/A",
        }));
    }, [surveyData, medicalStats]);

    const tripletCards = useMemo(() => {
        if (!medicalStats?.incidentTripletByCategory) return [];

        return Object.entries(medicalStats.incidentTripletByCategory).map(
            ([category, metrics]) => ({
                category,
                metrics,
            })
        );
    }, [medicalStats]);

    const getTopInjuriesData = (category) => {
        if (!medicalStats?.topInjuries?.[category]) return [];
        return medicalStats.topInjuries[category].sort((a, b) => a.total - b.total).map(item => ({
            y: item.bodyPart,
            x: item.total,
        }));
    };

    const getLeftMargin = (category) => {
        if (!medicalStats?.topInjuries?.[category]) return 120;
        // Find the longest body part label
        const maxLength = Math.max(...medicalStats.topInjuries[category].map(i => i.bodyPart.length));
        // Estimate pixels: ~8px per character + some padding
        return Math.min(200, 8 * maxLength + 20);
    };

    const getChartHeight = (category) => {
        const count = medicalStats?.topInjuries?.[category]?.length || 1;
        return Math.min(50 * count, 300); // 50px per bar, max 300px
    };

    const handleGotoIncident = (incidentId) => {
        window.location.href = `/#/incident/${incidentId}`;
    };

    const handleForceSync = async () => {
        setLoadingSync(true);
        const res = await medicalApi.forceSyncSurveyResponses();
        setLoadingSync(false);
        if (res.length > 0) window.location.reload();
    }

    const handleSendReminder = (rowData) => {
        confirmAlert({
            title: "Send Reminder",
            message: `Send reminder for ${rowData.fullName}?`,
            buttons: [
                {
                    label: "Yes",
                    onClick: async () => {
                        try {
                            const _ = await incident_manager.generate_send_qualtrics_links(rowData.incidentId, [rowData.riderId]);
                            window.location.reload();
                        } catch (error) {
                            console.error(error);
                            alert("Failed to send reminder");
                        }
                    },
                },
                {
                    label: "Cancel",
                },
            ],
        });
    };

    const formatDate = (date) => {
        if (!date) return "";
        return new Date(date).toLocaleDateString();
    };

    const getStatusChip = (rowData) => {
        switch (rowData['status']) {
            case "Responded":
                return (
                    <Chip
                        icon={<CheckCircleIcon />}
                        label="Responded"
                        className={classes.responded}
                        size="small"
                    />
                );
            case "Sent":
                return (
                    <Chip
                        icon={<SendIcon />}
                        label="Sent"
                        className={classes.sent}
                        size="small"
                    />
                );
            default:
                return (
                    <Chip
                        icon={<ScheduleIcon />}
                        label={`Reminder Sent on ${formatDate(rowData.reminderDate)}`}
                        className={classes.reminded}
                        size="small"
                    />
                );
        }
    };

    const chartHeights = tripletCards.map(card => getChartHeight(card.category));
    const maxChartHeight = Math.max(...chartHeights);

    return (
        <div key='race-container'>
            <h1>Medical reporting</h1>
            <Grid container spacing={2} style={{ marginBottom: 24 }}>
                {tripletCards.map((card) => {
                    const chartHeight = getChartHeight(card.category);
                    const paddingBottom = maxChartHeight - chartHeight;

                    return (
                        <Grid item xs={12} md={6} key={card.category}>
                            <Card style={{ paddingBottom }}>
                                <CardContent>
                                    <Typography
                                        className={classes.cardTitle}
                                        variant="h6"
                                    >
                                        {card.category} World Tour Summary
                                    </Typography>
                                    {card.metrics.map((m) => (
                                        <Typography
                                            key={m.metric}
                                            className={classes.cardMetric}
                                            variant="body2"
                                        >
                                            <strong>{m.metric}:</strong> {m.rate95CI} ({m.numerator}/{m.athleteDays})
                                        </Typography>
                                    ))}

                                    <Typography className={classes.cardTitle} variant="subtitle1" style={{ marginTop: 16 }}>
                                        Top Injuries
                                    </Typography>
                                    <FlexibleWidthXYPlot
                                        yType="ordinal"
                                        height={chartHeight}
                                        margin={{ left: getLeftMargin(card.category), right: 10, top: 10, bottom: 70 }}
                                    >
                                        <VerticalGridLines />
                                        <HorizontalGridLines />
                                        <XAxis tickFormat={v => Number.isInteger(v) ? v : ''} />
                                        <YAxis />
                                        <HorizontalBarSeries
                                            data={getTopInjuriesData(card.category)}
                                            color='rgb(0, 120, 216)'
                                        />
                                    </FlexibleWidthXYPlot>
                                </CardContent>
                            </Card>
                        </Grid>);
                }
            )}
            </Grid>
            <MaterialTable
                //title="Incident Overview"
                columns={[
                    { title: "Incident ID", field: "incidentId", type: "numeric" },
                    {
                        title: "CISS",
                        field: "ciss",
                        render: (rowData) =>
                            rowData.ciss != null
                                ? rowData.ciss.toFixed(4)
                                : "-",
                    },
                    { title: "Race", field: "raceName" },
                    {
                        title: "Incident Date",
                        field: "incidentDate",
                        type: "date",
                    },
                    { title: "Rider", field: "fullName" },
                    { title: "Team", field: "teamName" },
                    { title: "UCI ID", field: "uciId", type: "numeric" },
                    { title: "Sent To", field: "sentTo" },
                    {
                        title: "Response Date",
                        field: "responseDate",
                        render: (rowData) =>
                            rowData.responseDate
                                ? new Date(rowData.responseDate).toLocaleString()
                                : "-",
                    },
                    {
                        title: "Status",
                        field: "status",
                        render: (rowData) => getStatusChip(rowData),
                        lookup: {
                            Responded: "Responded",
                            Sent: "Sent",
                            Reminder: "Reminder Sent",
                        },
                    },
                ]}
                data={tableData}
                actions={[
                    {
                        icon: () =>
                            loadingSync ? (
                                <CircularProgress size={20} />
                            ) : (
                                <SyncIcon />
                            ),
                        tooltip: "Force Sync Responses",
                        isFreeAction: true, // puts button in toolbar
                        onClick: handleForceSync,
                    },
                    (rowData) => ({
                        icon: () => <NotificationsActiveIcon />,
                        tooltip: "Send Reminder",
                        disabled: rowData.status === "Responded",
                        onClick: (event, rowData) =>
                            handleSendReminder(rowData),
                    }),
                    (rowData) => ({
                        icon: () => <ArrowRightAltIcon />,
                        tooltip: "Go to incident",
                        onClick: (event, rowData) => handleGotoIncident(rowData['incidentId']),
                    }),
                ]}
                options={{
                    sorting: true,
                    filtering: true,
                    search: true,
                    paging: true,
                    pageSize: 10,
                    pageSizeOptions: [10, 20, 50, 100],
                    exportButton: false,
                    headerStyle: {
                        fontWeight: "bold",
                    },
                    rowStyle: {
                        fontSize: "14px",
                    },
                    actionsColumnIndex: -1,
                    showTitle: false,
                }}
            />
        </div>
    );
}